import { useState, useEffect } from 'react'
import { customerApi } from '../services/api'
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline'

const TRIGGER_OPTIONS = [
  { value: 'auto', label: 'Automatic', description: 'AI detects buying intent and captures leads automatically' },
  { value: 'manual', label: 'Manual Only', description: 'Leads are only captured via the contact form' },
  { value: 'intent', label: 'Intent-Based', description: 'Capture when specific intent signals are detected' },
]

const FIELD_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'company', label: 'Company' },
]

export default function LeadCaptureSettings({ onNavigate }) {
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      const res = await customerApi.get('/customer/lead-capture/config')
      setConfig(res.data.config)
    } catch (err) {
      console.error('Failed to load config:', err)
      setConfig({
        enabled: true,
        trigger: 'auto',
        required_fields: ['name', 'email'],
        optional_fields: ['phone', 'company'],
        custom_questions: [],
        notification_email: '',
        thank_you_message: "Thanks! We'll be in touch soon.",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await customerApi.put('/customer/lead-capture/config', { config })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('Failed to save config:', err)
    } finally {
      setSaving(false)
    }
  }

  const toggleField = (field, list) => {
    const required = [...(config.required_fields || [])]
    const optional = [...(config.optional_fields || [])]

    if (list === 'required') {
      if (required.includes(field)) {
        setConfig({ ...config, required_fields: required.filter((f) => f !== field) })
      } else {
        setConfig({
          ...config,
          required_fields: [...required, field],
          optional_fields: optional.filter((f) => f !== field),
        })
      }
    } else {
      if (optional.includes(field)) {
        setConfig({ ...config, optional_fields: optional.filter((f) => f !== field) })
      } else {
        setConfig({
          ...config,
          optional_fields: [...optional, field],
          required_fields: required.filter((f) => f !== field),
        })
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-slate-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    )
  }

  if (!config) return null

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onNavigate && (
          <button onClick={() => onNavigate('leads')} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
        )}
        <div>
          <h2 className="text-xl font-bold text-slate-900">Lead Capture Settings</h2>
          <p className="text-sm text-slate-500 mt-0.5">Configure how leads are captured from your widget</p>
        </div>
      </div>

      {/* Enable Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Enable Lead Capture</h3>
            <p className="text-xs text-slate-500 mt-0.5">Automatically detect and capture leads from widget conversations</p>
          </div>
          <button
            onClick={() => setConfig({ ...config, enabled: !config.enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              config.enabled ? 'bg-brand-600' : 'bg-slate-200'
            }`}
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
              config.enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Trigger Mode */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Capture Trigger</h3>
        <div className="space-y-2">
          {TRIGGER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                config.trigger === opt.value ? 'border-brand-300 bg-brand-50' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type="radio"
                name="trigger"
                value={opt.value}
                checked={config.trigger === opt.value}
                onChange={(e) => setConfig({ ...config, trigger: e.target.value })}
                className="mt-0.5 text-brand-600 focus:ring-brand-500"
              />
              <div>
                <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                <div className="text-xs text-slate-500">{opt.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Required / Optional Fields */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Form Fields</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Required</h4>
            {FIELD_OPTIONS.map((f) => (
              <label key={`req-${f.value}`} className="flex items-center gap-2 py-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(config.required_fields || []).includes(f.value)}
                  onChange={() => toggleField(f.value, 'required')}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-slate-700">{f.label}</span>
              </label>
            ))}
          </div>
          <div>
            <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Optional</h4>
            {FIELD_OPTIONS.map((f) => (
              <label key={`opt-${f.value}`} className="flex items-center gap-2 py-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(config.optional_fields || []).includes(f.value)}
                  onChange={() => toggleField(f.value, 'optional')}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-slate-700">{f.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Notification Email */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Notification Email</h3>
        <input
          type="email"
          value={config.notification_email || ''}
          onChange={(e) => setConfig({ ...config, notification_email: e.target.value })}
          placeholder="you@company.com"
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        <p className="text-xs text-slate-500 mt-1.5">Receive an email when a new lead is captured</p>
      </div>

      {/* Thank You Message */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Thank You Message</h3>
        <textarea
          value={config.thank_you_message || ''}
          onChange={(e) => setConfig({ ...config, thank_you_message: e.target.value })}
          rows={2}
          placeholder="Thanks! We'll be in touch soon."
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
        />
        <p className="text-xs text-slate-500 mt-1.5">Displayed to the visitor after their information is captured</p>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : saved ? (
            <>
              <CheckIcon className="h-4 w-4" />
              Saved!
            </>
          ) : 'Save Settings'}
        </button>
      </div>
    </div>
  )
}
